import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  GitCommit,
  LayoutDashboard,
  Link2,
  Settings,
  ShieldOff,
  SearchIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiClient,
  Deployment,
  DeploymentVersion,
  VersionHistory,
} from "../services/api-client";
import "../index.css";
import { LogoutButton } from "../components/LogoutButton";
import { useDeploymentAccess } from "../contexts/useDeploymentAccess";
import { useAPIToken } from "@eaigle/ui";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  highlightMatchedText,
  Search,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@eaigle/ui";
import { cn } from "@eaigle/utils/cn";

type ProductStatus = "down" | "old" | "good" | "notdeployed";

const PRODUCTS = [
  "AVAC Backend",
  "CV Backend",
  "Agent Dashboard",
  "AVAC Dashboard",
  "Drivers App",
  "Kiosk App",
] as const;

const PRODUCT_URL_FIELD: Record<(typeof PRODUCTS)[number], keyof Deployment> = {
  "AVAC Backend": "avacBackendURL",
  "CV Backend": "cvBackendURL",
  "Agent Dashboard": "agentDashboardURL",
  "AVAC Dashboard": "avacDashboardURL",
  "Drivers App": "driversAppURL",
  "Kiosk App": "kioskAppURL",
};

const THRESHOLD_OLD_DAYS = 45;
const THRESHOLD_DOWN_MS = 30 * 60 * 1000;
const THRESHOLD_OLD_MS = THRESHOLD_OLD_DAYS * 24 * 60 * 60 * 1000;

const STATUS_STYLES: Record<ProductStatus, string> = {
  down: "border-red-400 bg-red-300 text-red-900",
  old: "border-amber-400 bg-amber-300 text-amber-900",
  good: "border-emerald-400 bg-emerald-300 text-emerald-900",
  notdeployed: "border-gray-400 bg-gray-300 text-gray-900",
};

function getDefaultServiceUrl(
  subscriptionName: string,
  product: (typeof PRODUCTS)[number],
): string {
  switch (product) {
    case "AVAC Backend":
      return `https://${subscriptionName}-avac.azurewebsites.net`;
    case "CV Backend":
      return `https://${subscriptionName}-cv.azurewebsites.net`;
    case "Agent Dashboard":
      return "";
    case "AVAC Dashboard":
      return `https://avac.${subscriptionName}.eaigle.ai`;
    case "Drivers App":
      return `https://driverapp.${subscriptionName}.eaigle.ai`;
    case "Kiosk App":
      return `https://kiosk.${subscriptionName}.eaigle.ai`;
    default:
      return "";
  }
}

function resolveServiceUrl(
  deployment: Deployment | undefined,
  subscriptionName: string,
  product: (typeof PRODUCTS)[number],
): string {
  const field = PRODUCT_URL_FIELD[product];
  const stored = deployment ? String(deployment[field] ?? "") : "";
  return stored || getDefaultServiceUrl(subscriptionName, product);
}

function isNavigableUrl(url: string): boolean {
  return url.startsWith("http://") || url.startsWith("https://");
}

function buildVersionPageUrl(serviceUrl: string): string {
  return `${serviceUrl.replace(/\/$/, "")}/version`;
}

type CellActionsProps = {
  actionKey: string;
  serviceUrl: string;
  gitHash: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
};

function ActionSlot({ show, children }: { show: boolean; children: ReactNode }) {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center">
      {show ? children : <span className="block h-7 w-7" aria-hidden />}
    </div>
  );
}

function CellActions({
  actionKey,
  serviceUrl,
  gitHash,
  copiedKey,
  onCopy,
}: CellActionsProps) {
  const canOpenService = isNavigableUrl(serviceUrl);
  const versionUrl = canOpenService ? buildVersionPageUrl(serviceUrl) : "";

  return (
    <div className="mt-auto flex items-center justify-end gap-0.5 border-t border-black/5 pt-1.5">
      <ActionSlot show={canOpenService}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                window.open(serviceUrl, "_blank", "noopener,noreferrer");
              }}
              aria-label="Open service"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs break-all">Open service</TooltipContent>
        </Tooltip>
      </ActionSlot>
      <ActionSlot show={canOpenService}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                window.open(versionUrl, "_blank", "noopener,noreferrer");
              }}
              aria-label="Open version page"
            >
              <Link2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs break-all">Open /version</TooltipContent>
        </Tooltip>
      </ActionSlot>
      <ActionSlot show={Boolean(gitHash)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onCopy(`${actionKey}-hash`, gitHash);
              }}
              aria-label="Copy commit hash"
            >
              {copiedKey === `${actionKey}-hash` ?
                <Check className="h-3.5 w-3.5 text-emerald-700" />
              : <GitCommit className="h-3.5 w-3.5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {copiedKey === `${actionKey}-hash` ? "Copied hash!" : "Copy commit hash"}
          </TooltipContent>
        </Tooltip>
      </ActionSlot>
      <ActionSlot show={canOpenService}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onCopy(`${actionKey}-url`, versionUrl);
              }}
              aria-label="Copy version URL"
            >
              {copiedKey === `${actionKey}-url` ?
                <Check className="h-3.5 w-3.5 text-emerald-700" />
              : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {copiedKey === `${actionKey}-url` ? "Copied URL!" : "Copy version URL"}
          </TooltipContent>
        </Tooltip>
      </ActionSlot>
    </div>
  );
}

const LEGEND_ITEMS = [
  {
    label: "Down",
    description: "No poll in the last 30 minutes",
    icon: AlertTriangle,
    badgeClassName: "border-red-200 bg-red-300 text-red-800",
  },
  {
    label: `> ${THRESHOLD_OLD_DAYS} days old`,
    description: `Commit is older than ${THRESHOLD_OLD_DAYS} days`,
    icon: Clock,
    badgeClassName: "border-amber-200 bg-amber-300 text-amber-800",
  },
  {
    label: "Good",
    description: "Recently polled and up to date",
    icon: CheckCircle2,
    badgeClassName: "border-emerald-200 bg-emerald-300 text-emerald-800",
  },
  {
    label: "Not Deployed",
    description: "Component is not deployed",
    icon: CheckCircle2,
    badgeClassName: "border-dashed-gray-200 bg-gray-300 text-gray-800",
  },
];

const STATUS_CARD_MIN_HEIGHT = "min-h-[6.5rem]";

const STICKY_SUBSCRIPTION_CLASS =
  "sticky left-0 z-10 bg-white shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]";

const STICKY_SUBSCRIPTION_HEADER_CLASS =
  "sticky left-0 top-0 z-30 bg-white shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]";

const STICKY_PRODUCT_HEADER_CLASS =
  "sticky top-0 z-20 bg-gray-50 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.06)]";

function formatDate(value?: string): string {
  return (value ?? "").replace("T", " ").replace(/\..*/, "");
}

function formatHash(value?: string): string {
  return (value ?? "").substring(0, 8);
}

function parseDate(value?: string): Date {
  return new Date(value ?? "");
}

function findProduct(
  products: VersionHistory[],
  product: string,
): VersionHistory | undefined {
  return products.find((p) => p.product === product);
}

function getProductStatus(product?: VersionHistory): ProductStatus {
  const now = Date.now();
  if (
    !product?.lastPoll ||
    now - parseDate(product.lastPoll).getTime() > THRESHOLD_DOWN_MS
  ) {
    return "down";
  }
  if (
    !product?.commitDate ||
    now - parseDate(product.commitDate).getTime() > THRESHOLD_OLD_MS
  ) {
    return "old";
  }
  return "good";
}

const DeploymentVersionPage = () => {
  const navigate = useNavigate();
  const [histories, setHistories] = useState<DeploymentVersion[]>([]);
  const [deploymentsByName, setDeploymentsByName] = useState<Map<string, Deployment>>(
    new Map(),
  );
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [siteSearch, setSiteSearch] = useState("");
  const [pageError, setPageError] = useState<"forbidden" | "error" | null>(null);
  const { token } = useAPIToken();
  const { hasAdminAccess } = useDeploymentAccess();
  const copyTimeoutRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      return;
    }
    try {
      const [historyResponse, deploymentResponse] = await Promise.all([
        apiClient.getHistory(token),
        apiClient.getDeployments(token),
      ]);
      setHistories(historyResponse);
      setDeploymentsByName(
        new Map(deploymentResponse.map((d) => [d.subscriptionName, d])),
      );
      setPageError(null);
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      setPageError(status === 403 ? "forbidden" : "error");
    }
  }, [token]);

  const copyText = useCallback(async (key: string, text: string) => {
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedKey(null);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch {
      // clipboard unavailable
    }
  }, []);

  const filteredHistories = useMemo(() => {
    const query = siteSearch.trim().toLowerCase();
    if (!query) {
      return histories;
    }
    return histories.filter((history) =>
      history.subscriptionName.toLowerCase().includes(query),
    );
  }, [histories, siteSearch]);

  const settings = useCallback(() => {
    navigate("/settings", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(
      async () => {
        refresh();
      },
      5 * 60 * 1000,
    );
    refresh();

    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    const ref = copyTimeoutRef;
    return () => {
      if (ref.current !== null) window.clearTimeout(ref.current);
    };
  }, []);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex h-full min-h-0 flex-col gap-5 bg-secondary p-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
              <LayoutDashboard className="h-5 w-5 text-primary" aria-hidden />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Deployment versions</h1>
              <p className="mt-1 text-sm text-gray-600">
                Live view of deployed builds. Open services, copy hashes, or inspect
                version pages. Refreshes every 5 minutes.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Legend
              </span>
              {LEGEND_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <Tooltip key={item.label}>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={`gap-1.5 px-2.5 py-1 ${item.badgeClassName}`}
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden />
                        {item.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>{item.description}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>

            {hasAdminAccess && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={settings}
                    aria-label="Open deployment settings"
                  >
                    <Settings className="size-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Deployment settings</TooltipContent>
              </Tooltip>
            )}

            <LogoutButton />
          </div>
        </header>

        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden border-gray-200/80 shadow-sm">
          <CardHeader className="border-b border-gray-100 px-5 py-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Version matrix</CardTitle>
                <CardDescription>
                  Each cell shows deploy date and commit. Use the action icons to open the
                  service, view /version, or copy the hash and URL.
                </CardDescription>
              </div>
              <Search
                value={siteSearch}
                onChange={setSiteSearch}
                placeholder="Search site…"
                className="w-full sm:w-72"
                inputClassName="py-3"
                icon={<SearchIcon className="size-4 shrink-0 text-gray-400" />}
              />
            </div>
          </CardHeader>

          <CardContent className="flex min-h-0 flex-1 flex-col px-0 py-0">
            {pageError ?
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
                {pageError === "forbidden" ?
                  <>
                    <ShieldOff className="h-10 w-10 text-red-400" aria-hidden />
                    <p className="text-sm font-semibold text-gray-800">Access denied</p>
                    <p className="text-sm text-gray-500">
                      You don&apos;t have permission to view this data. Contact your
                      administrator.
                    </p>
                    <LogoutButton variant="labeled" />
                  </>
                : <>
                    <AlertTriangle className="h-10 w-10 text-amber-400" aria-hidden />
                    <p className="text-sm font-semibold text-gray-800">Failed to load</p>
                    <p className="text-sm text-gray-500">
                      Something went wrong while loading data. Try refreshing the page.
                    </p>
                  </>
                }
              </div>
            : <div className="scroll-area min-h-0 flex-1 overflow-auto">
                <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      <th
                        className={cn(
                          STICKY_SUBSCRIPTION_HEADER_CLASS,
                          "min-w-[180px] border-b border-r border-gray-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500",
                        )}
                      >
                        Site
                      </th>
                      {PRODUCTS.map((product) => (
                        <th
                          key={product}
                          className={cn(
                            STICKY_PRODUCT_HEADER_CLASS,
                            "min-w-[168px] border-b border-gray-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500",
                          )}
                        >
                          {product}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistories.map((history) => (
                      <tr key={history.subscriptionName} className="group">
                        <td
                          className={cn(
                            STICKY_SUBSCRIPTION_CLASS,
                            "border-b border-r border-gray-100 px-3 py-2 align-top font-medium text-gray-900 group-hover:bg-gray-50/80",
                          )}
                        >
                          <div className={`flex ${STATUS_CARD_MIN_HEIGHT} items-center`}>
                            {highlightMatchedText(history.subscriptionName, siteSearch)}
                          </div>
                        </td>
                        {PRODUCTS.map((prod) => {
                          const product = findProduct(history.products, prod);
                          const deployment = deploymentsByName.get(
                            history.subscriptionName,
                          );
                          const serviceUrl = resolveServiceUrl(
                            deployment,
                            history.subscriptionName,
                            prod,
                          );
                          const status = serviceUrl === "DISABLED" ? "notdeployed" : getProductStatus(product)
                          const actionKey = `${history.subscriptionName}-${prod}`;
                          const fullHash = product?.gitCommitHash ?? "";

                          return (
                            <td
                              key={history.subscriptionName + prod}
                              className="h-px border-b border-gray-100 p-2 align-top"
                            >
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={cn(
                                      "flex",
                                      STATUS_CARD_MIN_HEIGHT,
                                      "h-full flex-col rounded-lg border px-2.5 py-2 transition-colors",
                                      STATUS_STYLES[status],
                                    )}
                                  >
                                    <div className="text-xs font-medium tabular-nums">
                                      {formatDate(product?.commitDate) || "N/A"}
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-1 text-[11px] opacity-80">
                                      {isNavigableUrl(serviceUrl) ? <GitCommit
                                        className="h-3 w-3 shrink-0"
                                        aria-hidden
                                      />
                                      : null }
                                      <span className="font-mono">
                                        {formatHash(fullHash) || ""}
                                      </span>
                                    </div>
                                    <CellActions
                                      actionKey={actionKey}
                                      serviceUrl={serviceUrl}
                                      gitHash={fullHash}
                                      copiedKey={copiedKey}
                                      onCopy={copyText}
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-xs space-y-2 p-3 text-left"
                                >
                                  <p className="text-xs font-semibold">{prod}</p>
                                  {isNavigableUrl(serviceUrl) ?
                                    <p className="text-[11px] break-all opacity-80">
                                      {serviceUrl}
                                    </p>
                                  : null}
                                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                                    <dt className="text-gray-500">Last upgrade</dt>
                                    <dd className="font-mono tabular-nums">
                                      {product?.changedAt || "—"}
                                    </dd>
                                    <dt className="text-gray-500">Last poll</dt>
                                    <dd className="font-mono tabular-nums">
                                      {product?.lastPoll || "—"}
                                    </dd>
                                    <dt className="text-gray-500">Build date</dt>
                                    <dd className="font-mono tabular-nums">
                                      {product?.buildDate || "—"}
                                    </dd>
                                    <dt className="text-gray-500">Git hash</dt>
                                    <dd className="font-mono break-all">
                                      {fullHash || "—"}
                                    </dd>
                                    <dt className="text-gray-500">Commit date</dt>
                                    <dd className="font-mono tabular-nums">
                                      {product?.commitDate || "—"}
                                    </dd>
                                    <dt className="text-gray-500">Started</dt>
                                    <dd className="font-mono tabular-nums">
                                      {product?.applicationStarted || "—"}
                                    </dd>
                                  </dl>
                                </TooltipContent>
                              </Tooltip>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {histories.length === 0 && (
                  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 py-12 text-center">
                    <LayoutDashboard className="mb-3 h-8 w-8 text-gray-400" aria-hidden />
                    <p className="text-sm font-medium text-gray-700">
                      No deployment data
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Version history will appear here once sites report in.
                    </p>
                  </div>
                )}

                {histories.length > 0 && filteredHistories.length === 0 && (
                  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 py-12 text-center">
                    <SearchIcon className="mb-3 h-8 w-8 text-gray-400" aria-hidden />
                    <p className="text-sm font-medium text-gray-700">No matching sites</p>
                    <p className="mt-1 text-sm text-gray-500">
                      Try a different search term or clear the filter.
                    </p>
                  </div>
                )}
              </div>
            }
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default DeploymentVersionPage;
